import { InputAdornment, TextField, type TextFieldProps } from '@mui/material';
import type { ChangeEvent, ReactNode } from 'react';
import { getTokenPlaceholder, TokenDetail } from '@/utils/tokenNumberUtils';

interface DecimalInputFieldProps extends Omit<TextFieldProps, 'onChange' | 'value' | 'type' | 'inputMode'> {
  value: string;
  onChange: (value: string) => void;
  endAdornment?: ReactNode;
  maxPrecision?: number;
}

const LEADING_ZEROS_PATTERN = /^0+/;

/**
 * Coerce typed/pasted input into a bare decimal string: strips separators, currency, signs
 * (so "$1,234.56" → "1234.56") and keeps only the first decimal point.
 */
export function sanitizeDecimalInput(raw: string): string {
  const cleaned = raw.replace(/[^\d.]/g, '');
  const firstDot = cleaned.indexOf('.');
  if (firstDot === -1) {
    return cleaned;
  }
  // Keep the first dot, drop any subsequent ones (e.g. "1.2.3" → "1.23").
  return cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '');
}

export function formatDecimalInput(raw: string, maxPrecision?: number): string {
  let formattedValue = sanitizeDecimalInput(raw);

  // Truncate excess decimals instead of rejecting the value (pastes used to vanish silently).
  if (maxPrecision != null && formattedValue.includes('.')) {
    const [intPart, fracPart] = formattedValue.split('.');
    formattedValue = `${intPart}.${fracPart.slice(0, maxPrecision)}`;
  }

  if (formattedValue.length > 1 && formattedValue.startsWith('0') && !formattedValue.startsWith('0.')) {
    formattedValue = formattedValue.replace(LEADING_ZEROS_PATTERN, '');
  }

  return formattedValue;
}

export function DecimalInputField({
  value,
  onChange,
  endAdornment,
  maxPrecision = 2,
  placeholder,
  variant = 'outlined',
  size = 'small',
  fullWidth = true,
  ...textFieldProps
}: DecimalInputFieldProps) {
  const inputSx = {
    pl: 1.5,
    pr: 1.5,
    ...(textFieldProps?.slotProps?.input && 'sx' in textFieldProps.slotProps.input ? textFieldProps.slotProps.input.sx : {}),
  };

  const handleDecimalChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(formatDecimalInput(e.target.value, maxPrecision));
  };

  const finalPlaceholder = placeholder ?? getTokenPlaceholder(maxPrecision);

  return (
    <TextField
      {...textFieldProps}
      value={value}
      onChange={handleDecimalChange}
      type="text"
      inputMode="decimal"
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      placeholder={finalPlaceholder}
      label={null}
      slotProps={{
        ...textFieldProps.slotProps,
        htmlInput: {
          ...textFieldProps.slotProps?.htmlInput,
          sx: {
            ...(size === 'small' && { textAlign: 'right' }),
            height: '36px',
            py: 0,
          },
        },
        input: {
          ...textFieldProps.slotProps?.input,
          sx: inputSx,
          startAdornment: textFieldProps.label ? (
            <InputAdornment disablePointerEvents position="start">
              {textFieldProps.label}
            </InputAdornment>
          ) : undefined,
          endAdornment: endAdornment ? <InputAdornment position="end">{endAdornment}</InputAdornment> : null,
        },
      }}
    />
  );
}
